import { LocalStorage } from 'src/app/class/local-storage';
import { TranslateService } from '@ngx-translate/core'
import { Component, Input, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { ICompany } from 'src/app/interface/company';
import { AuthService } from 'src/app/service/auth/auth.service';
import { AlertService } from 'src/app/service/alert/alert.service';
import { ShiftService } from 'src/app/service/shift/shift.service';
import { AlertController, NavController, ModalController, IonRefresher } from '@ionic/angular';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { CrewUpdatedService } from 'src/app/event/crew-updated/crew-updated.service';
import { MessagesModalComponent } from 'src/app/modal/messages-modal/messages-modal.component';
import { UserUnreadMessagesUpdatedService } from 'src/app/event/user-unread-messages-updated/user-unread-messages-updated.service';
import { CONSTANTS } from 'src/app/config/constants';
import { ICondition } from 'src/app/interface/condition';
import { MenuService } from 'src/app/event/menu/menu.service';
import { CompanyService } from 'src/app/service/company/company.service';
import { LocalNotifications } from '@capacitor/local-notifications';
import { Push } from 'src/app/helper/push/push-helper';
import { Badge } from '@capawesome/capacitor-badge';
import { GeofenceNotificationsService } from 'src/app/service/geofence-notifications/geofence-notifications.service';
import { AnalyticsService } from 'src/app/service/analytics/analytics.service';


@Component({
	selector: 'app-menu',
	templateUrl: './menu.component.html',
	styleUrls: ['./menu.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class MenuComponent implements OnInit, OnDestroy {

	private ionRefresher?: IonRefresher;

	private unreadMessagesSubscription: Subscription = new Subscription();
	private menuServiceSubscription: Subscription = new Subscription();
	private crewUpdatedSubscription: Subscription = new Subscription();

	@Input()
	public parent?: string;

	public dateTime: Date;
	public requesting: boolean = false;
	public shiftButtonStatus = ShiftButtonStatus;
	public shiftButtonCurrentStatus: number = ShiftButtonStatus.NOT_READY;
	private notes: string;
	public forced: Array<string> = [];

	public set unreadMessages(value: number) {
		LocalStorage.set(CONSTANTS.localStorage.keys.menu.unreadMessages, value);
	}
	public get unreadMessages(): number {
		return LocalStorage.get(CONSTANTS.localStorage.keys.menu.unreadMessages);
	}

	public get condition(): ICondition {
		return LocalStorage.get(CONSTANTS.localStorage.keys.menu.condition);
	}

	public get companies(): Array<ICompany> {
		return LocalStorage.get(CONSTANTS.localStorage.keys.menu.companies);
	}

	public get company(): ICompany {
		return this.authService.company;
	}

	constructor(
		private authService: AuthService,
		private alertService: AlertService,
		private shiftService: ShiftService,
		private alertController: AlertController,
		private navController: NavController,
		private router: Router,
		private crewUpdatedService: CrewUpdatedService,
		private userUnreadMessagesUpdatedService: UserUnreadMessagesUpdatedService,
		private modalController: ModalController,
		private translateService: TranslateService,
		private menuService: MenuService,
		private changeDetectorRef: ChangeDetectorRef,
		private companyService: CompanyService,
		private geofenceNotificationsService: GeofenceNotificationsService,
		private analyticsService: AnalyticsService
	) {
		this.dateTime = new Date();
	}

	public ngOnInit(): void {
		this.subscribeMenu();
		this.subscribeCrew();
		this.subscribeUnreadMessages();
		this.menuService.callEndpoints();
	}

	public ngOnDestroy() {
		this.menuServiceSubscription?.unsubscribe();
		this.crewUpdatedSubscription?.unsubscribe();
		this.unreadMessagesSubscription?.unsubscribe();
	}

	private subscribeMenu() {
		this.menuServiceSubscription = this.menuService
			.updateViewListener()
			.subscribe(() => this.updateView());
	}

	private subscribeCrew() {
		this.crewUpdatedSubscription?.unsubscribe();
		this.crewUpdatedSubscription = this.crewUpdatedService
			.listener()
			.subscribe((data: any) => {
				const user = this.authService.user;
				this.updateView();
			});
	}

	private subscribeUnreadMessages() {
		this.unreadMessagesSubscription = this.userUnreadMessagesUpdatedService
			.listener()
			.subscribe(response => {
				this.unreadMessages = response.unread_messages;
				this.changeDetectorRef.detectChanges();
				this.updateBadge();
			});
	}

	private updateBadge() {
		if (this.unreadMessages) {
			Badge.set({ count: this.unreadMessages });
		} else {
			Badge.clear();
			LocalNotifications.removeAllDeliveredNotifications();
			Push.removeAllDeliveredNotifications();
		}
	}

	public companiesSelected(company: ICompany) {
		if (this.authService.company?.id === company.id) {
			return;
		}
		this.authService.company = company;
		this.companyService.change(company.id);
		this.navController.navigateRoot(['auth', 'home']);
	}

	public startShift() {
		if (!this.authService.company) {
			this.alertService.show(this.translateService.instant('Please, select a Company.'));
			return;
		}
		this.storeShift(true);
	}

	public async endShift() {
		const alert = await this.alertController.create({
			header: this.translateService.instant('End Shift'),
			subHeader: this.translateService.instant('Do you want to end your Shift?'),
			inputs: [
				{
					name: 'notes',
					placeholder: this.translateService.instant('Shift note...')
				},
			],
			buttons: [
				{
					text: this.translateService.instant('No'),
					role: 'no',
					cssClass: 'dialog-no',
				},
				{
					text: this.translateService.instant('Yes'),
					role: 'yes',
					cssClass: 'dialog-yes',
					handler: (data) => {
						if (data.notes && data.notes.length > 250) {
							alert.message = `<span class="alert-error-message">${this.translateService.instant('Cannot enter value greater that 250')}.</span>`;
							return false;
						}
						this.notes = data.notes;
						this.storeShift(false, this.notes);
						return true;
					},
				},
			],
		});
		alert.present();
	}

	private storeShift(start: boolean, notes?: string) {
		this.requesting = true;
		this.shiftService
			.store({ operators: start ? [{ id: this.authService.user.id }] : [], notes, forced: this.forced })
			.subscribe({
				next: (response) => {
					this.forced = [];
					this.requesting = false;
					this.crewUpdatedService.local(response.crew);
					this.alertService.toast(response.message);
					if (start) {
						this.analyticsService.trackShiftStarted(this.authService.user?.shift?.id);
					} else {
						this.analyticsService.trackShiftCompleted(this.authService.user?.shift?.id);
					}
					if (start && (this.router.url === '/auth' || this.router.url === '/auth/home')) {
						this.navController.navigateForward('/auth/onboard-equipment');
						return;
					}
					this.navController.navigateForward('/auth/home');

				},
				error: (error) => {
					this.requesting = false;
					if (error.error?.confirmation) {
						this.forced = error.error?.forced;

						this.alertService.forceConfirm(
							error.error?.message,
							this.translateService.instant('OK'),
							start ? this.translateService.instant('Force Start') : this.translateService.instant('Force End'),
							this.translateService.instant('Message')
						).then((value) => {
							if (!value) {
								this.forced.push(error.error?.confirmation);
								this.storeShift(false, this.notes);
							}
						});

						return;
					}
					this.alertService.error(error);
				}
			});
	}

	public async openMessagesModal(): Promise<void> {
		const modal = await this.modalController.create({ component: MessagesModalComponent });
		modal.onDidDismiss().then(() => this.menuService.callEndpoints());
		modal.present();
	}

	private updateView() {
		if (this.authService.user?.shift?.started_at && !this.authService.user?.shift?.ended_at) {
			this.shiftButtonCurrentStatus = ShiftButtonStatus.PLAY;
		} else if (this.authService.company) {
			this.shiftButtonCurrentStatus = ShiftButtonStatus.STOP;
		}
		this.changeDetectorRef.detectChanges();
		this.updateBadge();
	}

	public refresh(event: any = null) {
		if (event) {
			this.ionRefresher = event.target;
			setTimeout(() => {
				this.ionRefresher.complete();
				this.changeDetectorRef.detectChanges();
				this.updateBadge();
			}, 1000);
		}
		this.menuService.callEndpoints();
	}
}

enum ShiftButtonStatus {
	NOT_READY = -1,
	STOP = 0,
	PLAY = 1,
}
